import {
    Box,
    Caption,
    Checkbox,
    StatusBadge as ComponentsStatusBadge,
    deriveColumnWidth,
    LinkText,
    Status,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { t } from "i18next";
import React from "react";
import { CellProps, Column } from "react-table";

import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import Checked from "components/icons/Checked";
import IndeterminateBox from "components/icons/IndeterminateBox";
import Unchecked from "components/icons/Unchecked";
import KebabMenu from "components/kebab-menu/KebabMenu";
import tableStyle from "components/reports/erasure-reports-table.scss";
import StatusBadge from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import TextWithTooltip from "components/table/TextWithTooltip";
import { generateUserTypesToTranslationMap } from "components/users/add-user/AddUserForm";
import UserKebabMenu from "components/users/UserKebabMenu";
import style from "components/users/users.scss";
import { AUTH_USER_DELETE, AUTH_USER_EDIT } from "domain/authority";
import { ssoLoginMethodList } from "domain/tenants";
import { UserType } from "domain/user";
import { Group, UserTableData } from "domain/users";
import { LicensePoolList } from "services/licenses/LicensePoolService";
import { userSessionService } from "services/user/UserSessionService";
import { resolveColor } from "utils/commonFunctions";
import { formatDateWithoutTime } from "utils/format";

import testIds from "testIds.json";

const commonCellRendered = {
    disableResizing: false,
    disableSortBy: true,
    minWidth: 90,
    Cell: ({ value }: CellProps<Report>) => <>{value}</>,
};

export const createColumns = (
    isUsersTableFeatureEnabled: boolean,
    hasLicensePoolFeature: boolean,
    canAssignLicensePool: boolean,
    isLoggedInUserSelected: boolean,
    hasAllUsersSelected: () => boolean,
    updateUserState: (value: boolean) => void,
    isAnyUsersSelected: () => boolean,
    isUuidSelected: (uuid: string) => boolean,
    deselected: boolean,
    handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, uuid: string) => void,
    userUuid: string | undefined,
    licensePoolsList: LicensePoolList[],
    isUserTypeStandard: (userType: string) => boolean,
    onUserDelete: () => void,
    onUserEdit: () => void,
    selectedUser: (uuid: string) => void,
    showDetailsView: (value: boolean) => void,
    userTypeTooltipsTranslationMap: Map<UserType, string>,
    tableContainerRef: React.RefObject<HTMLDivElement>,
    userGroupAccess: boolean,
    licensePoolsAccess: boolean,
    showGroupInitials: (groups: Group[]) => React.JSX.Element
) => {
    const columns: Array<Column<UserTableData>> = [];

    //TODO 6873 Remove after Users table rewrite is done
    const createSelectionCheckbox = (uuid: string) => {
        return (
            <label className={tableStyle.container}>
                <input
                    data-testid={testIds.common.primaryView.table.selectRowCheckbox}
                    key={uuid}
                    name="uuids"
                    id={uuid}
                    type="checkbox"
                    checked={isUuidSelected(uuid) && !deselected}
                    value={uuid}
                    onChange={(e) => handleCheckboxChange(e, uuid)}
                />
                <span className={tableStyle.checkmark} />
            </label>
        );
    };

    hasLicensePoolFeature &&
        canAssignLicensePool &&
        (!isUsersTableFeatureEnabled
            ? columns.push({
                  Header: () => (
                      <div className={tableStyle.selectAllCheckbox}>
                          {!isLoggedInUserSelected ? (
                              <label className={tableStyle.selectAllCheckboxContainer}>
                                  <input
                                      data-testid={testIds.common.primaryView.table.selectAllCheckbox}
                                      type="checkbox"
                                      checked={hasAllUsersSelected()}
                                      onChange={(event) => {
                                          const allSelected = hasAllUsersSelected();
                                          if (event.target.checked) {
                                              updateUserState(true);
                                          } else {
                                              updateUserState(!allSelected);
                                          }
                                      }}
                                  />
                                  <div className={tableStyle.customCheckboxIcon}>
                                      {isAnyUsersSelected() ? (
                                          !hasAllUsersSelected() ? (
                                              <IndeterminateBox
                                                  backgroundColor={resolveColor(
                                                      "--app-buttons-buttonPrimary-backgroundColor"
                                                  )}
                                                  lineColor={resolveColor("--color-white")}
                                              />
                                          ) : (
                                              <Checked
                                                  backgroundColor={resolveColor(
                                                      "--app-buttons-buttonPrimary-backgroundColor"
                                                  )}
                                                  checkColor={resolveColor("--color-white")}
                                              />
                                          )
                                      ) : (
                                          <Unchecked
                                              borderColor={resolveColor("--app-tables-tableRowDividerColor")}
                                              backgroundColor={resolveColor("--app-checkbox-customCheckboxIcon")}
                                          />
                                      )}
                                  </div>
                              </label>
                          ) : (
                              ""
                          )}
                      </div>
                  ),
                  accessor: "checked",
                  disableSortBy: true,
                  minWidth: 70,
                  maxWidth: 70,
                  Cell: ({
                      cell: {
                          row: { original: userData },
                      },
                  }) => {
                      return (
                          <>
                              <div className={classNames(tableStyle.flexDisplay)}>
                                  <div className={classNames(style.tableDetailsGrid, tableStyle.gridContentLayout)}>
                                      <div className={tableStyle.flexDisplay}>
                                          {userUuid != userData.uuid && createSelectionCheckbox(userData.uuid)}
                                      </div>
                                  </div>
                              </div>
                          </>
                      );
                  },
              })
            : columns.push({
                  ...commonCellRendered,
                  id: "selection",
                  Header: () => (
                      <Checkbox
                          name={"multiselect"}
                          testIds={{ input: testIds.common.primaryView.table.selectAllCheckbox }}
                          checked={hasAllUsersSelected()}
                          indeterminate={isAnyUsersSelected() && !hasAllUsersSelected()}
                          onChange={(event) => {
                              if (event.target.checked) {
                                  updateUserState(true);
                              } else {
                                  updateUserState(!hasAllUsersSelected());
                              }
                          }}
                          hideLabelSection
                      />
                  ),
                  Cell: ({ row }: CellProps<UserTableData>) => {
                      const isLoggedInUser = userUuid === row.original.uuid;

                      if (isLoggedInUser) {
                          return null;
                      }

                      return (
                          <Checkbox
                              testIds={{ input: testIds.common.primaryView.table.selectRowCheckbox }}
                              key={row.original.uuid}
                              id={row.original.uuid}
                              name="uuids"
                              checked={isUuidSelected(row.original.uuid) && !deselected}
                              value={row.original.uuid}
                              onChange={(e) => handleCheckboxChange(e, row.original.uuid)}
                              hideLabelSection
                          />
                      );
                  },
                  disableResizing: true,
                  minWidth: 60,
                  width: 60,
              }));

    columns.push({
        Header: isUsersTableFeatureEnabled ? (
            <TextCell key="name">{t("Common.name")}</TextCell>
        ) : (
            <TextWithTooltip text={t("Common.name")} key="name" />
        ),
        accessor: "name",
        Cell: (cellInfo) =>
            //TODO BCC-6868 Remove isUsersTableFeatureEnabled chek after Users table rewrite is done.
            isUsersTableFeatureEnabled ? (
                <TextCell>
                    <Box verticalAlignment="Center" gap={"Xxs"}>
                        <UserKebabMenu
                            uuid={cellInfo.cell.row.original.uuid}
                            userName={
                                cellInfo.value == null || cellInfo.value.trim() === ""
                                    ? cellInfo.cell.row.original.email
                                    : cellInfo.value
                            }
                            userRoleUuid={cellInfo.cell.row.original.roleUuid}
                            email={cellInfo.cell.row.original.email}
                            expirationDate={cellInfo.cell.row.original.expirationDate}
                            onUserDelete={onUserDelete}
                            onUserEdit={onUserEdit}
                            enabled={cellInfo.cell.row.original.enabled}
                            emailVerified={cellInfo.cell.row.original.emailVerified}
                            loginMethod={cellInfo.cell.row.original.loginMethod}
                            userLicensePoolUuid={cellInfo.cell.row.original.poolName}
                            userType={cellInfo.cell.row.original.userType}
                            licensePoolsList={licensePoolsList}
                            tableRef={tableContainerRef}
                            //BCC-6868  Remove isUsersTableFeatureEnabled from UserKebabMenu's props when Users table rewrite is done.
                            isUsersTableFeatureEnabled={isUsersTableFeatureEnabled}
                        />
                        <Box
                            columnLayout={!isUserTypeStandard(cellInfo.cell.row.original.userType)}
                            horizontalAlignment={"Left"}
                            verticalAlignment={"Top"}
                        >
                            <LinkText
                                as={"button"}
                                onClick={() => {
                                    selectedUser(cellInfo.cell.row.original.uuid);
                                    showDetailsView(true);
                                }}
                            >
                                {cellInfo.value == null || cellInfo.value?.trim() === ""
                                    ? cellInfo.cell.row.original.email
                                    : cellInfo.value}
                            </LinkText>
                            {!isUserTypeStandard(cellInfo.cell.row.original.userType) && (
                                <Tooltip key={"userTypeTooltip"}>
                                    <TooltipTrigger>
                                        <Caption greyTextStyle>
                                            {generateUserTypesToTranslationMap(t).get(
                                                cellInfo.cell.row.original.userType
                                            )}
                                        </Caption>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {userTypeTooltipsTranslationMap.get(cellInfo.cell.row.original.userType)}
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </TextCell>
            ) : (
                <div className={classNames(tableStyle.flexDisplay)}>
                    {userSessionService.userHasAnyAuthority([AUTH_USER_DELETE, AUTH_USER_EDIT]) &&
                        userUuid != cellInfo.cell.row.original.uuid && (
                            <div className={tableStyle.flexDisplay}>
                                <KebabMenu>
                                    <UserKebabMenu
                                        uuid={cellInfo.cell.row.original.uuid}
                                        userName={
                                            cellInfo.value == null || cellInfo.value.trim() === ""
                                                ? cellInfo.cell.row.original.email
                                                : cellInfo.value
                                        }
                                        userRoleUuid={cellInfo.cell.row.original.roleUuid}
                                        email={cellInfo.cell.row.original.email}
                                        expirationDate={cellInfo.cell.row.original.expirationDate}
                                        onUserDelete={onUserDelete}
                                        onUserEdit={onUserEdit}
                                        enabled={cellInfo.cell.row.original.enabled}
                                        emailVerified={cellInfo.cell.row.original.emailVerified}
                                        loginMethod={cellInfo.cell.row.original.loginMethod}
                                        userLicensePoolUuid={cellInfo.cell.row.original.poolName}
                                        userType={cellInfo.cell.row.original.userType}
                                        licensePoolsList={licensePoolsList}
                                        tableRef={tableContainerRef}
                                        //BCC-6868  Remove isUsersTableFeatureEnabled from UserKebabMenu's props when Users table rewrite is done.
                                        isUsersTableFeatureEnabled={isUsersTableFeatureEnabled}
                                    />
                                </KebabMenu>
                            </div>
                        )}
                    <div
                        className={
                            !isUserTypeStandard(cellInfo.cell.row.original.userType)
                                ? style.nameContainer
                                : style.nameContainerStandardUser
                        }
                    >
                        <button
                            className={style.userNameCell}
                            onClick={() => {
                                selectedUser(cellInfo.cell.row.original.uuid);
                                showDetailsView(true);
                            }}
                        >
                            <TextWithTooltip
                                text={
                                    cellInfo.value == null || cellInfo.value?.trim() === ""
                                        ? cellInfo.cell.row.original.email
                                        : cellInfo.value
                                }
                            />
                        </button>
                        {!isUserTypeStandard(cellInfo.cell.row.original.userType) && (
                            <TextWithTooltip
                                text={userTypeTooltipsTranslationMap.get(cellInfo.cell.row.original.userType)}
                                key={"userTypeTooltip"}
                            >
                                <span className={style.userTypeContainer}>
                                    {generateUserTypesToTranslationMap(t).get(cellInfo.cell.row.original.userType)}
                                </span>
                            </TextWithTooltip>
                        )}
                    </div>
                </div>
            ),
        width: isUsersTableFeatureEnabled ? 220 : deriveColumnWidth(25, tableContainerRef),
    });

    userGroupAccess &&
        columns.push({
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="groups">{t("UsersTable.groups")}</TextCell>
            ) : (
                <TextWithTooltip text={t("UsersTable.groups")} key="groups" />
            ),
            accessor: "groups",
            Cell: ({ cell: { value } }) => <div>{showGroupInitials(value ? value : [])}</div>,
            width: isUsersTableFeatureEnabled ? 180 : deriveColumnWidth(18, tableContainerRef),
        });
    columns.push(
        {
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="status">{t("UsersTable.userStatus.title")}</TextCell>
            ) : (
                <TextWithTooltip text={t("UsersTable.userStatus.title")} key="status" />
            ),
            accessor: "enabled",
            Cell: ({ cell: { value } }) =>
                isUsersTableFeatureEnabled ? (
                    <ComponentsStatusBadge
                        value={
                            value
                                ? t("UsersTable.userStatus.confirmed")
                                : t("UsersTable.userStatus.forceChangePassword")
                        }
                        status={value ? Status.SUCCESS : Status.ERROR}
                    />
                ) : (
                    <StatusBadge
                        values={[
                            value
                                ? {
                                      status: Status.SUCCESS,
                                      title: t("UsersTable.userStatus.confirmed"),
                                  }
                                : {
                                      status: Status.ERROR,
                                      title: t("UsersTable.userStatus.forceChangePassword"),
                                  },
                        ]}
                        tooltip={true}
                    />
                ),
            width: isUsersTableFeatureEnabled ? 150 : deriveColumnWidth(12, tableContainerRef),
        },
        {
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="email">{t("Common.email")}</TextCell>
            ) : (
                <TextWithTooltip text={t("Common.email")} key="email" />
            ),
            accessor: "email",
            Cell: ({ cell: { value } }) =>
                isUsersTableFeatureEnabled ? (
                    <TextCell>
                        <Box>
                            {value}
                            <CopyToClipboard value={value} isNewTable />
                        </Box>
                    </TextCell>
                ) : (
                    <div className={style.alignment}>
                        <TextWithTooltip text={value} />
                        <CopyToClipboard value={value} />
                    </div>
                ),
            width: isUsersTableFeatureEnabled ? 150 : deriveColumnWidth(28, tableContainerRef),
        },
        {
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="emailVerified">{t("UsersTable.emailStatus")}</TextCell>
            ) : (
                <TextWithTooltip text={t("UsersTable.emailStatus")} key="emailVerified" />
            ),
            accessor: "emailVerified",
            Cell: ({ row }) =>
                isUsersTableFeatureEnabled ? (
                    ssoLoginMethodList.includes(row.original.loginMethod) ? (
                        <ComponentsStatusBadge status={Status.INFO} value={t("UsersTable.ssoUsers")} />
                    ) : row.original.emailVerified ? (
                        <ComponentsStatusBadge status={Status.SUCCESS} value={t("UsersTable.emailVerified")} />
                    ) : (
                        <ComponentsStatusBadge status={Status.ERROR} value={t("UsersTable.emailUnverified")} />
                    )
                ) : (
                    <StatusBadge
                        values={[
                            ssoLoginMethodList.includes(row.original.loginMethod)
                                ? {
                                      status: Status.INFO,
                                      title: t("UsersTable.ssoUsers"),
                                  }
                                : row.original.emailVerified
                                  ? {
                                        status: Status.SUCCESS,
                                        title: t("UsersTable.emailVerified"),
                                    }
                                  : {
                                        status: Status.ERROR,
                                        title: t("UsersTable.emailUnverified"),
                                    },
                        ]}
                        tooltip={true}
                    />
                ),
            width: isUsersTableFeatureEnabled ? 150 : deriveColumnWidth(12, tableContainerRef),
        },
        {
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="createdDate">{t("UsersTable.createdDate")}</TextCell>
            ) : (
                <TextWithTooltip text={t("UsersTable.createdDate")} key="createdDate" />
            ),
            accessor: "createdDate",
            Cell: ({ cell: { value } }) =>
                isUsersTableFeatureEnabled ? (
                    <TextCell>{formatDateWithoutTime(value)}</TextCell>
                ) : (
                    <DateCell tooltip={true} value={value} withoutTime={true} />
                ),
            width: isUsersTableFeatureEnabled ? 150 : deriveColumnWidth(18, tableContainerRef),
        },
        {
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="expirationDate">{t("UsersTable.expirationDate")}</TextCell>
            ) : (
                <TextWithTooltip text={t("UsersTable.expirationDate")} key="expirationDate" />
            ),
            accessor: "expirationDate",
            Cell: ({ cell: { value } }) =>
                isUsersTableFeatureEnabled ? (
                    <TextCell>{value ? formatDateWithoutTime(value) : t("Common.never")}</TextCell>
                ) : value ? (
                    <DateCell tooltip={true} value={value} withoutTime={true} />
                ) : (
                    t("Common.never")
                ),
            width: isUsersTableFeatureEnabled ? 150 : deriveColumnWidth(18, tableContainerRef),
        }
    );

    licensePoolsAccess &&
        columns.push({
            Header: isUsersTableFeatureEnabled ? (
                <TextCell key="pool_name">{t("UsersTable.licensePoolAssigned")}</TextCell>
            ) : (
                <TextWithTooltip text={t("UsersTable.licensePoolAssigned")} key="pool_name" />
            ),
            accessor: "poolName",
            Cell: ({ cell: { value } }) =>
                isUsersTableFeatureEnabled ? (
                    <TextCell>{value}</TextCell>
                ) : (
                    <div className={style.alignment}>
                        <TextWithTooltip text={value} />
                    </div>
                ),
            width: isUsersTableFeatureEnabled ? 150 : deriveColumnWidth(12, tableContainerRef),
        });

    return columns;
};
